import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { useTranslation } from "../../i18n";
import CampaignForm, { clampNumber } from "./CampaignForm";
import CopySqlButton from "./CopySqlButton";
import toast from "react-hot-toast";

/**
 * One modal for creating a campaign and for editing one.
 *
 * The settings of a trigger type are a long, schema-driven form (templates,
 * segments, caps, schedule). Rendering it inline above the list meant the
 * page grew a second and third block as the operator clicked around, and the
 * campaign being edited was not obvious. In a dialog the form has exactly one
 * home and an explicit title, and the list below stays the thing you navigate
 * by.
 *
 * It fills the screen: the template editor with its preview, the segment
 * picker and the schedule each need room, and a form tall enough to force a
 * scrollbar inside the modal plus one on the dialog itself is worse than no
 * modal at all. The body scrolls; the header and the actions stay put.
 *
 * Settings are not thrown away silently. A campaign is a few dozen typed
 * characters, and Esc or an outside click used to drop a finished form with no
 * word — so a close is confirmed once the settings differ from where the
 * dialog opened, and a reload/close of the tab asks the browser the same
 * question. The name and the trigger type are excluded: picking a type seeds
 * the config with the schema defaults, and asking "discard?" over that would
 * make the prompt fire on every empty open.
 *
 * Pass `campaign` to edit it, omit it to create a new one.
 */
export default function CampaignFormDialog({
  open,
  onOpenChange,
  campaign = null,
  triggerTypes,
  segmentOptions = [],
  saving = false,
  onCreate,
  onUpdate,
}) {
  const { t } = useTranslation();
  const isEdit = campaign != null;

  const [name, setName] = useState("");
  const [triggerType, setTriggerType] = useState("");
  const [config, setConfig] = useState({});
  // The config as it stood on the open edge, kept as a snapshot. A refetch of
  // the campaign list — another editor's save, a mutation made from the list
  // below — hands this component a new `campaign` object, and a baseline read
  // off it would move under the operator: their config would suddenly compare
  // equal to the server's and the guard would let a close drop the difference
  // with no word. Frozen at open, it answers only "what was here before I
  // typed anything".
  const [baselineConfig, setBaselineConfig] = useState({});

  // Load whatever is being edited when the dialog opens, and only then. The
  // campaign list is invalidated by every mutation, so reloading on each
  // `campaign` change would replace whatever the operator had typed with the
  // server's copy — the form must stay open until it is closed.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const stored = campaign?.config || {};
      setName(campaign?.name || "");
      setTriggerType(campaign?.triggerType || "");
      setConfig(stored);
      setBaselineConfig(stored);
    }
    wasOpen.current = open;
  }, [open, campaign]);

  const meta = triggerTypes.find((tt) => tt.triggerType === triggerType);
  const fields = meta?.configFields || [];
  const placeholders = meta?.placeholders || [];

  // What the settings looked like when this dialog opened: that snapshot when
  // editing, the schema defaults of the chosen trigger when creating — the
  // same defaults the picker seeds on a type change, so an untouched new
  // campaign is not "dirty". Everything the operator typed past that is what a
  // close must not silently drop.
  const baseline = isEdit ? baselineConfig : configFromSchema(fields);
  const dirty = open && !sameConfig(config, baseline);

  // The browser owns the wording of this one; returning a non-empty string
  // from the handler is what makes it show its "Leave site?" prompt.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const requestClose = (next) => {
    // Radix routes Esc, the overlay click and the ✕ through here, so this one
    // guard covers every way out of the dialog.
    if (!next && dirty && !window.confirm(t("campaign.discardChanges"))) {
      return;
    }
    onOpenChange(next);
  };

  // A schedule-kind trigger has nothing but the scheduler to run it, so an
  // empty schedule is a campaign that can never fire. Saying so here spares
  // the operator a round trip and a toast — it stays an informational hint.
  //
  // Only a trigger that asks for a schedule gets the hint: when the cadence is
  // the platform's (see `fixed_schedule_cron` in the trigger), the schema
  // carries no cron field at all, and complaining about the empty one it never
  // rendered told the operator to fill in a field that is not on the form.
  const hasScheduleField = fields.some((f) => f.name === "schedule_cron");
  const scheduleEmpty =
    Boolean(meta) &&
    meta.kind === "schedule" &&
    hasScheduleField &&
    !String(config.schedule_cron || "").trim();

  // The save is blocked only where the backend would refuse it. That check
  // re-runs on a changed config alone (`_config_is_rechecked` in
  // campaigns.py), so a campaign stored without a schedule — before the rule
  // existed, or written through the API — has to stay renameable and editable
  // here too, or this dialog locks it out of its only edit path.
  const scheduleMissing =
    scheduleEmpty && (!isEdit || !sameConfig(config, campaign?.config ?? {}));

  const handleSave = async () => {
    if (!triggerType) {
      toast.error(t("campaign.selectTrigger"));
      return;
    }
    // The name is how the campaign is told apart in the list, the header and
    // the segment usage report — an unnamed one is a row of trigger labels.
    if (!name.trim()) {
      toast.error(t("campaign.nameRequired"));
      return;
    }
    if (scheduleMissing) {
      toast.error(t("campaign.scheduleRequired", { trigger: meta.label }));
      return;
    }
    try {
      // Clamp every numeric setting to the range its schema declares, so a
      // value the form never should have held (a negative holdout percent, a
      // negative pacing delay) cannot reach the config.
      const savedConfig = normaliseNumbers(config, fields);
      if (isEdit) {
        await onUpdate(campaign.id, {
          config: savedConfig,
          name: name.trim(),
          enabled: campaign.enabled,
        });
      } else {
        await onCreate({
          trigger_type: triggerType,
          config: savedConfig,
          name: name.trim(),
        });
      }
      // Saved, not discarded — close without asking.
      onOpenChange(false);
    } catch (err) {
      // apiFetch callers throw the response body, which is where the backend
      // puts the reason ("push_title_template must not be empty").
      toast.error(describe(err, t("campaign.toastCreateFailed")));
    }
  };

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      {/* The dialog itself must not scroll: only the form body between the
          header and the actions does. `!` because the base class carries
          `overflow-y-auto`, which tailwind-merge does not treat as the same
          group — without it the whole dialog scrolled and the footer left
          with the page. */}
      <DialogContent className="max-w-6xl h-[88dvh] max-h-[88dvh] !overflow-hidden grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("campaign.editTitle", {
                  name:
                    campaign.name ||
                    t("campaign.campaignFallback", { id: campaign.id }),
                })
              : t("campaign.createTitle")}
          </DialogTitle>
          {meta?.description && (
            <DialogDescription>{meta.description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto pr-1 space-y-3">
          <div className="space-y-1">
            <Label htmlFor="campaign-name">
              {t("campaign.name")}
              <span className="text-red-400"> *</span>
            </Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("campaign.namePlaceholder")}
              disabled={saving}
            />
            <p className="text-c-gray-500 text-xs">
              {t("campaign.nameRequiredHint")}
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="campaign-trigger">
              {t("campaign.triggerType")}
            </Label>
            <Select
              id="campaign-trigger"
              value={triggerType}
              // A campaign's trigger type is immutable: its schema, its segment
              // keys and its analytics SQL all follow from it, so changing it
              // would silently reinterpret every stored setting.
              disabled={saving || isEdit}
              onChange={(e) => {
                const type = e.target.value;
                setTriggerType(type);
                // Seed the config from the schema so what the form shows is
                // what gets saved. A display-only fallback let an untouched
                // form submit `config: {}` and be rejected as missing its
                // push template.
                setConfig(
                  type ? configFromSchema(fieldsFor(triggerTypes, type)) : {},
                );
              }}
            >
              <option value="">—</option>
              {triggerTypes.map((tt) => (
                <option key={tt.triggerType} value={tt.triggerType}>
                  {tt.label}
                </option>
              ))}
            </Select>
          </div>

          {triggerType && (
            <>
              <div className="flex justify-end">
                <CopySqlButton
                  triggerType={triggerType}
                  config={config}
                  campaignId={campaign?.id}
                />
              </div>

              <CampaignForm
                fields={fields}
                config={config}
                onChange={setConfig}
                disabled={saving}
                triggerType={triggerType}
                placeholders={placeholders}
                campaignId={campaign?.id ?? null}
                segmentOptions={segmentOptions}
              />

              {scheduleEmpty && (
                <p className="text-amber-400 text-xs">
                  {t("campaign.scheduleRequired", { trigger: meta.label })}
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => requestClose(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={saving || scheduleMissing}>
            {isEdit ? t("common.save") : t("campaign.createBtn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Fields of a trigger type, empty until one is picked. */
function fieldsFor(triggerTypes, triggerType) {
  return (
    triggerTypes.find((tt) => tt.triggerType === triggerType)?.configFields ||
    []
  );
}

/**
 * Build a config from a trigger's field schema.
 *
 * Only fields that actually declare a default are included, so the saved
 * config stays explicit without inventing keys the trigger does not use.
 */
function configFromSchema(fields) {
  const config = {};
  for (const field of fields) {
    if (field.default !== undefined && field.default !== null) {
      config[field.name] = field.default;
    }
  }
  return config;
}

/**
 * Whether two configs are the same, the way the backend compares them.
 *
 * `PUT /campaigns/{id}` only re-checks the schedule rule when the submitted
 * config differs from the stored one, so the dialog has to mean the same thing
 * by "unchanged" or it would refuse a save the server accepts.
 */
function sameConfig(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, i) => sameConfig(item, b[i]))
    );
  }
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((k) => Object.hasOwn(b, k) && sameConfig(a[k], b[k]))
  );
}

function describe(err, fallback) {
  if (typeof err === "string" && err.trim()) return err;
  if (err?.message) return err.message;
  return fallback;
}

/**
 * A config whose numeric fields are clamped to their declared range.
 *
 * Fields are looked up by name across the whole schema (trigger fields and the
 * universal delivery controls alike), and a cleared field is left as null so
 * the backend applies its own default rather than reading a blank as 0.
 */
function normaliseNumbers(config, fields) {
  const byName = new Map(fields.map((field) => [field.name, field]));
  const result = { ...config };
  for (const [name, value] of Object.entries(result)) {
    const field = byName.get(name);
    if (!field || field.fieldType !== "number") continue;
    if (value === "" || value === null || value === undefined) {
      result[name] = null;
      continue;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      result[name] = null;
      continue;
    }
    result[name] = clampNumber(parsed, field);
  }
  return result;
}
